/**
 * Types for Nova AI Chat Component
 */

import { Engine } from '@/lib/engine';

/**
 * Interface for tool call
 */
export interface ToolCall {
  id?: string;
  name: string;
  parameters: Record<string, unknown>;
  result?: SQLQueryResponse | SchemaResponse;
  state?: 'running' | 'waiting' | 'completed' | 'error';
}

/**
 * Subscription information for the user
 */
export interface SubscriptionInfo {
  /**
   * Whether the user has an active subscription
   */
  hasActiveSubscription: boolean;
  
  /**
   * Additional subscription properties can be added here in the future
   * For example: quotaRemaining, quotaLimit, plan, etc.
   */
}

/**
 * Props for the NovaAIChat component
 */
export interface NovaAIChatProps {
  /** 
   * Whether the chat panel is visible 
   * @deprecated No longer used as the chat is now a permanent sidebar
   */
  isVisible?: boolean;
  /** 
   * Method to close the chat panel 
   * @deprecated No longer used as the chat is now a permanent sidebar
   */
  onClose?: () => void;
  /** Optional class name */
  className?: string;
  /** 
   * URL for the chat endpoint 
   * @default '/api/chat'
   */
  chatEndpoint?: string;
  /** 
   * Engine instance to use for query execution and schema retrieval
   */
  engine: Engine;
  /**
   * Callback function to run a SQL query in the Nova component
   * @param query The SQL query to run
   */
  onRunSqlQuery?: (query: string) => void;
  /**
   * Initial schema data to use for the chat
   * This is used to initialize the conversation with schema information
   */
  initialSchema?: SchemaResponse | null;
  /**
   * Subscription information for the user
   * Used to control access to AI chat features based on subscription status
   */
  subscriptionInfo?: SubscriptionInfo;
  /**
   * Optional callback when the upgrade link is clicked
   * Used to show the upgrade modal or navigate to upgrade page
   * @param args Object containing the openTrigger source
   */
  onUpgradeClick?: (args: { openTrigger: 'chat_suggestion' | 'upgrade_button' | 'pricing_card' }) => void;
  /**
   * Optional initial prompt to pre-populate the chat input field
   * This allows the chat to start with a pre-filled question or prompt
   */
  initialPrompt?: string;
}

/**
 * SQL Query Response
 */
export interface SQLQueryResponse {
  success: boolean;
  data?: Record<string, any>[];
  columns?: { name: string; type: string }[];
  rowCount?: number;
  message?: string;
  error?: string;
  query?: string;
}

/**
 * Schema Response
 */
export interface SchemaResponse {
  success: boolean;
  tables?: {
    name: string;
    columns: {
      name: string;
      type: string;
      nullable?: boolean;
      default?: string;
      maxLength?: number;
    }[];
  }[];
  error?: string;
}

/**
 * Define the supernova color theme
 */
export const supernovaColor = {
  main: '#8A2BE2', // Vibrant purple
  light: '#9D50BB',
  lighter: '#EDE7F6',
  dark: '#6A1B9A',
  border: 'rgba(138, 43, 226, 0.3)',
  background: 'rgba(138, 43, 226, 0.05)',
}; 